import { useCallback, useEffect, useRef, useState } from 'react';
import { supabase } from '@/utils/supabase';
import { createComponentLogger } from '@/utils/logger';
import type { PricingSaveField } from './usePricingDraft';

const log = createComponentLogger('useGroupedPricingSave', 'pricing-v2');

/**
 * Client hook for Phase 2 grouped pricing saves.
 *
 * Responsibilities:
 *   - Issue a single `save-group` POST to enqueue-encompass-write with a
 *     client-generated groupId. Same groupId on retry = server returns current
 *     state via the staging RPC's idempotent path.
 *   - Issue `cancel-group` when the user cancels pre-push.
 *   - Subscribe to pricing_write_groups via Supabase Realtime so waiting-unlock
 *     and retry states can resolve without a full page refresh.
 *   - Hold an activeGroup ref/state so save status can remain visible after
 *     the draft clears back to authoritative encompass_loans values.
 *
 * Not this hook's job:
 *   - Reading/writing draft state → usePricingDraft owns that.
 *   - Firing pricing_field_changed emails. Lifecycle row-diff owns that after
 *     Encompass readback updates encompass_loans.
 */

export type GroupStatus =
  | 'pending'
  | 'pushing'
  | 'waiting_unlock'
  | 'waiting_retry'
  | 'validated'
  | 'cancelled'
  | 'failed';

export interface ActiveGroup {
  id: string;
  status: GroupStatus;
  createdAt: string;
  /** Last status transition; lets the bar offer Retry for a `pending` group the worker never claimed. */
  updatedAt: string;
  /** Set when the save also switched the active delivery lock (cannot be cancelled). */
  activateLockVersionId: string | null;
  /**
   * False only for a save that carries the Investor Loan # alone (no pricing
   * field, no lock switch): that number is already saved in the portal while
   * Encompass catches up. Unknown groups count as pricing.
   */
  containsPricingChanges: boolean;
}

export interface SaveRevision {
  /** Raw `encompass_loans.updated_at` string, no Date round-trip. */
  loanUpdatedAt: string | null;
  /** The lo_compensation pricing item's reductionBps, null when the item does not exist. */
  locrBps: number | null;
  /** Sum of non-legacy concession points as loaded by useGateLocals. */
  concessionPoints: number;
}

export type SaveOkStatus =
  | 'pending'
  | 'pushing'
  | 'waiting_unlock'
  | 'waiting_retry'
  | 'validated';

export interface SaveRequestOptions {
  fields: PricingSaveField[];
  changeReason?: string;
  priceFinalizedEmailRequested?: boolean;
  /**
   * When the workspace is in active delivery-lock mode, the caller passes
   * the active selection ID so server-side validation (LOCR cap, lender-credit
   * cap) reads `active_delivery_locks -> lock_versions.rate_lock_fields`
   * instead of live `encompass_loans` lock fields. Keeps server validation
   * basis aligned with the lock version the user is actually editing against.
   * `null` or omitted → server uses live lock fields (live-mode default).
   */
  deliveryLockSelectionId?: string | null;
  /**
   * Candidate mode (Set Active & Save): validate against this lock version,
   * stage the write and switch the active lock in one server transaction.
   * Mutually exclusive with `deliveryLockSelectionId`.
   */
  targetLockVersionId?: string | null;
  /** The active selection the screen is showing; the server refuses if it changed. */
  expectedActiveLockVersionId?: string | null;
  /** Pricing inputs the projection was computed from; the server refuses if they changed. */
  revision?: SaveRevision;
}

export interface SaveOk {
  ok: true;
  groupId: string;
  status: SaveOkStatus;
  lockedFieldIds?: string[];
  failedFieldIds?: string[];
  retryingFieldIds?: string[];
  missingFieldIds?: string[];
  /** Present when the group switched the active delivery lock. */
  activateLockVersionId?: string | null;
}

export interface SaveErr {
  ok: false;
  error: string;
  fieldId?: string;
  conflictingGroupId?: string;
  conflictingGroupStatus?: string;
  capPercent?: number;
  detail?: string;
  // QA follow-up — surfaced on LOCR_EXCEEDS_BUILT_IN_COMP and
  // LENDER_CREDIT_EXCEEDS_AVAILABLE so the toast can render the cap in copy.
  maxBps?: number;
  maxGenericLenderCreditDollars?: number;
  maxTotalLenderCreditDollars?: number;
  itemizedLenderCreditDollars?: number;
  /** BELOW_PAR: how far the projection lands under par. */
  shortfallPoints?: number;
  shortfallDollars?: number;
  /** HTTP status of a non-2xx response, when known. */
  httpStatus?: number;
  /**
   * Set when a 5xx / network failure came back but the group row exists on
   * the server: staging committed before the response was lost. The caller
   * must treat the submitted values as saved.
   */
  recoveredGroup?: ActiveGroup;
}

export type SaveResult = SaveOk | SaveErr;

export interface CancelReverted {
  fieldId: string;
  previousLocalValue: string | null;
}

export interface CancelResult {
  ok: boolean;
  error?: string;
  groupStatus?: string;
  reason?: string;
  /** Populated on ok:true — server's reverted field values for local draft reset. */
  reverted?: CancelReverted[];
}

export interface UseGroupedPricingSaveArgs {
  loanId: string;
  encompassLoanId: string;
  /** Fired when Encompass write success is recorded (`validated` legacy DB status). */
  onValidated?: (groupId: string) => void;
}

export interface UseGroupedPricingSaveOutput {
  save: (opts?: SaveRequestOptions) => Promise<SaveResult>;
  retry: (groupId: string) => Promise<SaveResult>;
  cancel: (groupId: string) => Promise<CancelResult>;
  isSaving: boolean;
  isCancelling: boolean;
  /** Populated only while a save is still pending, retryable, or briefly saved. */
  activeGroup: ActiveGroup | null;
}

const TERMINAL_GROUP_STATUSES = new Set<GroupStatus>(['validated', 'cancelled', 'failed']);
const REUSABLE_GROUP_STATUSES = new Set<GroupStatus>([
  'pending',
  'pushing',
  'waiting_unlock',
  'waiting_retry'
]);
const ACTIVE_NONTERMINAL_GROUP_STATUSES = new Set<GroupStatus>([
  'pending',
  'pushing',
  'waiting_unlock',
  'waiting_retry'
]);
const ACTIVE_GROUP_STATUS_LIST: GroupStatus[] = [
  'pending',
  'pushing',
  'waiting_unlock',
  'waiting_retry'
];
const GROUP_RECONCILE_FAST_INTERVAL_MS = 1000;
const GROUP_RECONCILE_SLOW_AFTER_MS = 30_000;
const GROUP_RECONCILE_SLOW_INTERVAL_MS = 5000;
const GROUP_ROW_COLUMNS = 'id, status, created_at, updated_at, activate_lock_version_id, contains_pricing_changes';

interface GroupRow {
  id: string;
  status: string;
  created_at?: string | null;
  updated_at?: string | null;
  activate_lock_version_id?: string | null;
  contains_pricing_changes?: boolean | null;
}

/** The Investor Loan # (Encompass 352) is the one grouped field that is not pricing. */
function savesPricingChanges(fields: PricingSaveField[], activatesLock: boolean): boolean {
  return activatesLock || fields.some((f) => f.fieldId !== '352');
}

function groupFromRow(row: GroupRow): ActiveGroup {
  const now = new Date().toISOString();
  return {
    id: row.id,
    status: row.status as GroupStatus,
    createdAt: row.created_at ?? now,
    updatedAt: row.updated_at ?? row.created_at ?? now,
    activateLockVersionId: row.activate_lock_version_id ?? null,
    containsPricingChanges: row.contains_pricing_changes !== false
  };
}

/** A 5xx or a lost response: staging may have committed before the reply. */
function isAmbiguousSaveFailure(result: SaveErr): boolean {
  return result.error === 'NETWORK_ERROR' || (typeof result.httpStatus === 'number' && result.httpStatus >= 500);
}

/**
 * Safely read the JSON body out of a failing `supabase.functions.invoke` call.
 *
 * `@supabase/functions-js@2.x` throws `FunctionsHttpError` for any non-2xx
 * response (see node_modules/@supabase/functions-js/src/FunctionsClient.ts:115).
 * The invoke return shape on failure is `{ data: null, error, response }` —
 * `data` is NEVER populated from the error response body. The JSON payload
 * (including our server's `{ ok: false, error: 'ENCOMPASS_LOAN_ID_MISMATCH', ... }`
 * shape) lives only on the Response object, available as either the top-level
 * `response` field or `error.context` (same Response instance stored as
 * FunctionsHttpError.context). `.clone()` so we don't consume the stream for
 * any downstream consumer.
 */
async function readInvokeErrorBody(
  error: unknown,
  response: Response | undefined
): Promise<Record<string, unknown> | null> {
  const resp =
    response ??
    ((error as { context?: unknown } | null)?.context instanceof Response
      ? ((error as { context: Response }).context)
      : undefined);
  if (!(resp instanceof Response)) {
    return null;
  }
  try {
    return (await resp.clone().json()) as Record<string, unknown>;
  } catch {
    return null;
  }
}

/**
 * Invoke enqueue-encompass-write with a typed action body. On non-2xx, extract
 * the Edge Function's structured error body from the Response so the caller
 * sees `{ ok: false, error: 'LOCR_LOOKUP_FAILED', ... }` instead of the
 * generic FunctionsHttpError message.
 */
async function invokeSaveGroup(body: {
  action: 'save-group';
  loanId: string;
  encompassLoanId: string;
  groupId: string;
  fields: PricingSaveField[];
  changeReason?: string;
  priceFinalizedEmailRequested?: boolean;
  deliveryLockSelectionId?: string | null;
  targetLockVersionId?: string | null;
  expectedActiveLockVersionId?: string | null;
  revision?: SaveRevision;
}): Promise<SaveResult> {
  try {
    const { data, error, response } = await supabase.functions.invoke<SaveResult>(
      'enqueue-encompass-write',
      { body }
    );
    if (error) {
      const parsed = await readInvokeErrorBody(error, response);
      if (parsed && parsed.ok === false && typeof parsed.error === 'string') {
        return { ...(parsed as unknown as SaveErr), httpStatus: response?.status };
      }
      // No HTTP response at all (FunctionsFetchError, relay failure): the
      // request may have reached the server, so treat it as ambiguous and let
      // the caller re-hydrate by the minted group id.
      const hasResponse =
        response instanceof Response
        || (error as { context?: unknown } | null)?.context instanceof Response;
      if (!hasResponse) {
        log.warn('save-group transport failure', { metadata: { errorMessage: error.message } });
        return { ok: false, error: 'NETWORK_ERROR', detail: error.message };
      }
      // Parse failure or non-structured body — surface the generic message.
      log.warn('save-group non-2xx with unparseable body', {
        httpStatus: response?.status,
        errorMessage: error.message
      });
      return { ok: false, error: error.message ?? 'SAVE_FAILED', httpStatus: response?.status };
    }
    if (!data) {
      return { ok: false, error: 'EMPTY_RESPONSE' };
    }
    return data;
  } catch (e) {
    log.error('save-group invoke threw', {}, e as Error);
    return { ok: false, error: 'NETWORK_ERROR', detail: (e as Error).message };
  }
}

async function invokeRetryGroup(body: {
  action: 'retry-group';
  loanId: string;
  encompassLoanId: string;
  groupId: string;
}): Promise<SaveResult> {
  try {
    const { data, error, response } = await supabase.functions.invoke<SaveResult>(
      'enqueue-encompass-write',
      { body }
    );
    if (error) {
      const parsed = await readInvokeErrorBody(error, response);
      if (parsed && parsed.ok === false && typeof parsed.error === 'string') {
        return parsed as unknown as SaveErr;
      }
      log.warn('retry-group non-2xx with unparseable body', {
        httpStatus: response?.status,
        errorMessage: error.message
      });
      return { ok: false, error: error.message ?? 'RETRY_FAILED' };
    }
    if (!data) {
      return { ok: false, error: 'EMPTY_RESPONSE' };
    }
    return data;
  } catch (e) {
    log.error('retry-group invoke threw', {}, e as Error);
    return { ok: false, error: 'NETWORK_ERROR', detail: (e as Error).message };
  }
}

async function invokeCancelGroup(body: {
  action: 'cancel-group';
  loanId: string;
  encompassLoanId: string;
  groupId: string;
}): Promise<CancelResult> {
  try {
    const { data, error, response } = await supabase.functions.invoke<CancelResult>(
      'enqueue-encompass-write',
      { body }
    );
    if (error) {
      const parsed = await readInvokeErrorBody(error, response);
      if (parsed && parsed.ok === false && typeof parsed.error === 'string') {
        return parsed as unknown as CancelResult;
      }
      log.warn('cancel-group non-2xx with unparseable body', {
        httpStatus: response?.status,
        errorMessage: error.message
      });
      return { ok: false, error: error.message ?? 'CANCEL_FAILED' };
    }
    if (!data) {
      return { ok: false, error: 'EMPTY_RESPONSE' };
    }
    return data;
  } catch (e) {
    log.error('cancel-group invoke threw', {}, e as Error);
    return { ok: false, error: 'NETWORK_ERROR' };
  }
}

export function useGroupedPricingSave(
  args: UseGroupedPricingSaveArgs
): UseGroupedPricingSaveOutput {
  const { loanId, encompassLoanId, onValidated } = args;
  const [isSaving, setIsSaving] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [activeGroup, setActiveGroup] = useState<ActiveGroup | null>(null);

  // Stable ref for the success callback so the realtime subscription doesn't
  // churn on parent re-renders that pass a fresh callback each time.
  const onValidatedRef = useRef(onValidated);
  const activeGroupRef = useRef<ActiveGroup | null>(activeGroup);
  // Synchronous in-flight guard: `isSaving` is state and lags a fast second
  // click, which would mint a second group id. A repeat call gets
  // SAVE_IN_FLIGHT, never the first call's result: its values were not sent.
  const inFlightSaveRef = useRef<Promise<SaveResult> | null>(null);
  const validatedGroupIdRef = useRef<string | null>(null);
  const clearTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const clearTimerGroupIdRef = useRef<string | null>(null);
  useEffect(() => {
    onValidatedRef.current = onValidated;
  }, [onValidated]);
  useEffect(() => {
    activeGroupRef.current = activeGroup;
  }, [activeGroup]);

  useEffect(() => {
    if (!loanId || !encompassLoanId) {
      setActiveGroup(null);
      return;
    }

    let cancelled = false;
    setActiveGroup(null);
    const hydrate = async () => {
      const { data, error } = await supabase
        .from('pricing_write_groups')
        .select(GROUP_ROW_COLUMNS)
        .eq('loan_id', loanId)
        .eq('encompass_loan_id', encompassLoanId)
        .in('status', ACTIVE_GROUP_STATUS_LIST)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (cancelled) {
        return;
      }
      if (error) {
        log.warn('active group hydrate failed', { loanId, encompassLoanId, error: error.message });
        return;
      }
      if (data) {
        setActiveGroup(groupFromRow(data as GroupRow));
      }
    };

    void hydrate();
    return () => {
      cancelled = true;
    };
  }, [loanId, encompassLoanId]);

  const scheduleValidatedClear = useCallback((groupId: string) => {
    if (clearTimerRef.current !== null && clearTimerGroupIdRef.current === groupId) {
      return;
    }
    if (clearTimerRef.current !== null) {
      clearTimeout(clearTimerRef.current);
    }
    clearTimerGroupIdRef.current = groupId;
    clearTimerRef.current = setTimeout(() => {
      clearTimerRef.current = null;
      clearTimerGroupIdRef.current = null;
      if (validatedGroupIdRef.current === groupId) {
        validatedGroupIdRef.current = null;
      }
      setActiveGroup((prev) => (prev && prev.id === groupId ? null : prev));
    }, 2000);
  }, []);

  const handleValidated = useCallback((groupId: string) => {
    if (validatedGroupIdRef.current !== groupId) {
      validatedGroupIdRef.current = groupId;
      onValidatedRef.current?.(groupId);
    }
    scheduleValidatedClear(groupId);
  }, [scheduleValidatedClear]);

  const handleCancelled = useCallback((groupId: string) => {
    if (clearTimerRef.current !== null && clearTimerGroupIdRef.current === groupId) {
      clearTimeout(clearTimerRef.current);
      clearTimerRef.current = null;
      clearTimerGroupIdRef.current = null;
    }
    if (validatedGroupIdRef.current === groupId) {
      validatedGroupIdRef.current = null;
    }
    setActiveGroup((prev) => (prev && prev.id === groupId ? null : prev));
  }, []);

  useEffect(() => () => {
    if (clearTimerRef.current !== null) {
      clearTimeout(clearTimerRef.current);
    }
  }, []);

  // Realtime subscription on the active group. Resubscribes when activeGroup.id
  // changes; tears down on unmount or when we clear activeGroup. Captured as
  // a local so the exhaustive-deps lint check matches what's actually read.
  const activeGroupId = activeGroup?.id;
  useEffect(() => {
    if (!activeGroupId) {
      return;
    }

    const initialStatus = activeGroupRef.current?.status;
    if (!initialStatus || TERMINAL_GROUP_STATUSES.has(initialStatus)) {
      return;
    }

    let pollTimer: ReturnType<typeof setTimeout> | null = null;
    let pollStopped = false;
    const pollStartedAt = Date.now();

    const stopPolling = () => {
      pollStopped = true;
      if (pollTimer !== null) {
        clearTimeout(pollTimer);
        pollTimer = null;
      }
    };

    const nextPollDelay = () =>
      Date.now() - pollStartedAt < GROUP_RECONCILE_SLOW_AFTER_MS
        ? GROUP_RECONCILE_FAST_INTERVAL_MS
        : GROUP_RECONCILE_SLOW_INTERVAL_MS;

    const reconcile = (status: GroupStatus) => {
      setActiveGroup((prev) =>
        prev && prev.id === activeGroupId && prev.status !== status ? { ...prev, status } : prev
      );
      if (status === 'validated') {
        stopPolling();
        handleValidated(activeGroupId);
      } else if (status === 'cancelled') {
        stopPolling();
        handleCancelled(activeGroupId);
      } else if (status === 'failed' || !ACTIVE_NONTERMINAL_GROUP_STATUSES.has(status)) {
        stopPolling();
      }
    };

    const fetchLatestStatus = async () => {
      if (pollStopped) {
        return;
      }
      const { data, error } = await supabase
        .from('pricing_write_groups')
        .select('status')
        .eq('id', activeGroupId)
        .maybeSingle();
      if (pollStopped) {
        return;
      }
      if (error || !data) {
        scheduleNextPoll();
        return;
      }

      const status = data.status as GroupStatus;
      reconcile(status);
      if (ACTIVE_NONTERMINAL_GROUP_STATUSES.has(status)) {
        scheduleNextPoll();
      }
    };

    const scheduleNextPoll = () => {
      if (pollStopped) {
        return;
      }
      if (pollTimer !== null) {
        clearTimeout(pollTimer);
      }
      pollTimer = setTimeout(() => {
        pollTimer = null;
        void fetchLatestStatus();
      }, nextPollDelay());
    };

    const channel = supabase
      .channel(`pricing_write_groups:${activeGroupId}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'pricing_write_groups',
          filter: `id=eq.${activeGroupId}`
        },
        (payload) => {
          const next = payload.new as GroupRow;
          setActiveGroup((prev) =>
            prev && prev.id === activeGroupId
              ? {
                  ...prev,
                  updatedAt: next.updated_at ?? prev.updatedAt,
                  activateLockVersionId: next.activate_lock_version_id ?? prev.activateLockVersionId
                }
              : prev
          );
          reconcile(next.status as GroupStatus);
        }
      )
      .subscribe();

    if (ACTIVE_NONTERMINAL_GROUP_STATUSES.has(initialStatus)) {
      void fetchLatestStatus();
    }

    return () => {
      stopPolling();
      supabase.removeChannel(channel);
    };
  }, [activeGroupId, handleValidated, handleCancelled]);

  const submitSave = useCallback(
    async (opts?: SaveRequestOptions): Promise<SaveResult> => {
      const fields = opts?.fields ?? [];
      if (fields.length === 0) {
        return { ok: false, error: 'NOTHING_TO_SAVE' };
      }
      const containsPricingChanges = savesPricingChanges(fields, Boolean(opts?.targetLockVersionId));

      // Reuse an in-flight group's id on retry. If the caller hits save twice
      // while the first is still pending, the second hits the RPC's idempotent
      // retry path and returns the current state without re-staging.
      const groupId =
        activeGroup && REUSABLE_GROUP_STATUSES.has(activeGroup.status)
          ? activeGroup.id
          : crypto.randomUUID();

      setIsSaving(true);
      try {
        const body: Parameters<typeof invokeSaveGroup>[0] = {
          action: 'save-group',
          loanId,
          encompassLoanId,
          groupId,
          fields,
          changeReason: opts?.changeReason,
          priceFinalizedEmailRequested: opts?.priceFinalizedEmailRequested
        };
        if (opts?.targetLockVersionId) {
          body.targetLockVersionId = opts.targetLockVersionId;
          body.expectedActiveLockVersionId = opts.expectedActiveLockVersionId ?? null;
          if (opts.revision) {
            body.revision = opts.revision;
          }
        } else {
          body.deliveryLockSelectionId = opts?.deliveryLockSelectionId;
        }
        const result = await invokeSaveGroup(body);

        if (result.ok) {
          const now = new Date().toISOString();
          setActiveGroup({
            id: result.groupId,
            status: result.status as GroupStatus,
            createdAt: now, // best-effort; refined by realtime
            updatedAt: now,
            activateLockVersionId: result.activateLockVersionId ?? null,
            containsPricingChanges
          });
          // Immediate write success: notify the consumer without waiting for
          // realtime to echo the same terminal status.
          if (result.status === 'validated') {
            handleValidated(result.groupId);
          }
        } else if (
          result.error === 'ACTIVE_GROUP_IN_FLIGHT'
          && result.conflictingGroupId
          && result.conflictingGroupStatus
        ) {
          const now = new Date().toISOString();
          setActiveGroup({
            id: result.conflictingGroupId,
            status: result.conflictingGroupStatus as GroupStatus,
            createdAt: now,
            updatedAt: now,
            activateLockVersionId: null,
            containsPricingChanges: true
          });
        } else if (isAmbiguousSaveFailure(result)) {
          // The response was lost after the server may have committed the
          // group (e.g. the worker claim failed after staging). The group id
          // is client-minted, so a row with this id is this save.
          const { data, error } = await supabase
            .from('pricing_write_groups')
            .select(GROUP_ROW_COLUMNS)
            .eq('id', groupId)
            .maybeSingle();
          if (error) {
            log.warn('post-failure group re-hydrate failed', { metadata: { groupId, error: error.message } });
          } else if (data) {
            const recovered = groupFromRow(data as GroupRow);
            if (ACTIVE_NONTERMINAL_GROUP_STATUSES.has(recovered.status) || recovered.status === 'validated') {
              setActiveGroup(recovered);
              if (recovered.status === 'validated') {
                handleValidated(recovered.id);
              }
              return { ...result, recoveredGroup: recovered };
            }
          }
        }
        return result;
      } finally {
        setIsSaving(false);
      }
    },
    [loanId, encompassLoanId, activeGroup, handleValidated]
  );

  const save = useCallback<UseGroupedPricingSaveOutput['save']>(
    (opts) => {
      if (inFlightSaveRef.current) {
        return Promise.resolve<SaveResult>({ ok: false, error: 'SAVE_IN_FLIGHT' });
      }
      const submission = submitSave(opts);
      inFlightSaveRef.current = submission;
      const release = () => {
        if (inFlightSaveRef.current === submission) {
          inFlightSaveRef.current = null;
        }
      };
      // Release on either outcome; the caller still receives a rejection.
      void submission.then(release, release);
      return submission;
    },
    [submitSave]
  );

  const retry = useCallback<UseGroupedPricingSaveOutput['retry']>(
    async (groupId) => {
      setIsSaving(true);
      try {
        const result = await invokeRetryGroup({
          action: 'retry-group',
          loanId,
          encompassLoanId,
          groupId
        });
        if (result.ok) {
          const prev = activeGroupRef.current?.id === result.groupId ? activeGroupRef.current : null;
          const now = new Date().toISOString();
          setActiveGroup({
            id: result.groupId,
            status: result.status as GroupStatus,
            createdAt: prev?.createdAt ?? now,
            updatedAt: now,
            activateLockVersionId: result.activateLockVersionId ?? prev?.activateLockVersionId ?? null,
            containsPricingChanges: prev?.containsPricingChanges ?? true
          });
          if (result.status === 'validated') {
            handleValidated(result.groupId);
          }
        } else if (
          result.error === 'ACTIVE_GROUP_IN_FLIGHT'
          && result.conflictingGroupId
          && result.conflictingGroupStatus
        ) {
          const now = new Date().toISOString();
          setActiveGroup({
            id: result.conflictingGroupId,
            status: result.conflictingGroupStatus as GroupStatus,
            createdAt: now,
            updatedAt: now,
            activateLockVersionId: null,
            containsPricingChanges: true
          });
        }
        return result;
      } finally {
        setIsSaving(false);
      }
    },
    [loanId, encompassLoanId, handleValidated]
  );

  const cancel = useCallback<UseGroupedPricingSaveOutput['cancel']>(
    async (groupId) => {
      setIsCancelling(true);
      try {
        const result = await invokeCancelGroup({
          action: 'cancel-group',
          loanId,
          encompassLoanId,
          groupId
        });
        if (result.ok) {
          handleCancelled(groupId);
        }
        return result;
      } finally {
        setIsCancelling(false);
      }
    },
    [loanId, encompassLoanId, handleCancelled]
  );

  return {
    save,
    retry,
    cancel,
    isSaving,
    isCancelling,
    activeGroup
  };
}
